import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Wallet, Smartphone, ArrowRight } from 'lucide-react';
import { resolveReturnTo, isSafePath } from '../lib/paymentReturn';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders a modal that lets the user choose OMT or Whish Money, then calls
 * POST /api/payments/initiate. After success the user is routed to
 * /payments/{id} where they see the reference + instructions.
 *
 * Props:
 *   open              boolean — controls dialog visibility
 *   onOpenChange(v)   dialog close callback
 *   packageId         string  — required
 *   packageKind       "subscription" | "physio" — default "subscription"
 *   packageNameEn     string  — for display
 *   packageNameAr     string
 *   amount            number
 */
export const PaymentProviderDialog = ({
  open,
  onOpenChange,
  packageId,
  packageKind = 'subscription',
  packageNameEn,
  packageNameAr,
  amount,
  returnTo,
}) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/payments/providers`)
      .then((r) => setProviders(r.data || []))
      .catch(() => setProviders([]));
  }, [open]);

  const iconFor = (name) => (name === 'omt' ? <Wallet size={22} /> : <Smartphone size={22} />);

  const handleConfirm = async () => {
    if (!selected) {
      setError(t('اختر طريقة دفع', 'Please choose a payment method', 'Choisissez'));
      return;
    }
    if (!packageId) return;
    setError('');
    setLoading(true);
    // Where the user lands after paying: their doctors/pharmacists list for
    // call packages, or the booking form for physio / psychology.
    let return_to = null;
    try {
      const candidate = resolveReturnTo({ packageKind, packageId, explicit: returnTo });
      if (isSafePath(candidate)) return_to = candidate;
    } catch { /* ignore */ }
    try {
      const res = await axios.post(
        `${API}/payments/initiate`,
        { provider: selected, package_id: packageId, package_kind: packageKind, return_to },
        { withCredentials: true }
      );
      const payment = res.data;
      // Persist the return path keyed by payment id — used by
      // PaymentSuccessPage as a fallback when the server-side value is
      // unavailable (e.g. legacy pending payments before this feature).
      if (return_to && payment?.id) {
        try {
          sessionStorage.setItem(`payment_return_to_${payment.id}`, return_to);
        } catch { /* storage may be blocked — safe to ignore */ }
      }
      // If the provider returned a hosted checkout URL — redirect immediately.
      if (payment.checkout_url) {
        onOpenChange(false);
        window.location.href = payment.checkout_url;
        return;
      }
      // If the provider returned a friendly error (Whish API unreachable),
      // keep the dialog open and show it. The user can retry.
      if (payment?.instructions?.error) {
        setError(payment.instructions.error);
        toast.error(payment.instructions.error);
        return;
      }
      // Fallback — OMT manual receipt flow still uses the status page.
      onOpenChange(false);
      navigate(`/payments/${payment.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || t('فشل بدء الدفع', 'Failed to start payment', 'Échec');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={language === 'ar' ? 'rtl' : 'ltr'} data-testid="payment-provider-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {t('اختر طريقة الدفع', 'Choose payment method', 'Choisir le mode de paiement')}
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-1">
            {language === 'ar' ? packageNameAr : packageNameEn}
            {amount ? <span className="ms-2 font-bold text-[#1e88e5]">${amount}</span> : null}
          </p>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid="payment-dialog-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3 py-2">
          {providers.map((p) => (
            <button
              key={p.name}
              type="button"
              onClick={() => setSelected(p.name)}
              data-testid={`provider-option-${p.name}`}
              className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition
                ${selected === p.name
                  ? 'border-[#1e88e5] bg-blue-50 shadow'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
              `}
            >
              <div className="flex items-center gap-3">
                <span className={`p-2 rounded-full ${selected === p.name ? 'bg-[#1e88e5] text-white' : 'bg-gray-100 text-gray-700'}`}>
                  {iconFor(p.name)}
                </span>
                <div className="text-start">
                  <div className="font-bold text-[#2c3e50]">
                    {language === 'ar' ? p.display_name_ar : p.display_name_en}
                  </div>
                  <div className="text-xs text-gray-500">
                    {p.name === 'whish' && p.api_configured
                      ? t('دفع فوري عبر التطبيق', 'Instant payment link', 'Lien de paiement')
                      : t('ادفع من فرع أو تطبيق ثم أكّد الطلب', 'Pay via branch/app then confirm', 'Paiement manuel')}
                  </div>
                </div>
              </div>
              <ArrowRight size={16} className={selected === p.name ? 'text-[#1e88e5]' : 'text-gray-400'} />
            </button>
          ))}
          {providers.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              {t('لا توجد وسائل دفع متاحة حالياً', 'No payment methods available', 'Aucun mode disponible')}
            </p>
          )}
        </div>

        {selected === 'whish' && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800" data-testid="whish-secure-note">
            {t('سيتم توجيهك إلى صفحة الدفع الآمنة الخاصة بويش لإكمال الدفع.',
              'You will be redirected to the secure Whish payment page to complete your payment.',
              'Vous serez redirigé vers la page de paiement sécurisée Whish.')}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="payment-dialog-cancel">
            {t('إلغاء', 'Cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !selected}
            className="bg-[#1e88e5] hover:bg-[#1976d2]"
            data-testid="payment-dialog-confirm"
          >
            {loading
              ? t('جاري...', 'Starting...', 'Démarrage...')
              : (selected === 'whish'
                  ? t('متابعة إلى الدفع الآمن', 'Continue to Secure Payment', 'Continuer')
                  : t('متابعة', 'Continue', 'Continuer'))
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentProviderDialog;
